import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, formatErr, fmtDate } from "@/lib/api";
import { NotePencil, Lock, Globe, Trash, PaperPlaneTilt } from "@phosphor-icons/react";

/**
 * Job notes with permission-based visibility.
 *  - "shared": visible to everyone on the job (requires jobs.edit permission).
 *  - "private": visible only to the author (any user who can open the job).
 */
export function JobNotesCard({ jobId, canShare, currentUserId }) {
  const qc = useQueryClient();
  const { data: notes = [] } = useQuery({
    queryKey: ["job-notes", jobId],
    queryFn: () => http.get(`/jobs/${jobId}/notes`).then((r) => r.data),
  });

  const [text, setText] = useState("");
  const [visibility, setVisibility] = useState(canShare ? "shared" : "private");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true); setErr("");
    try {
      await http.post(`/jobs/${jobId}/notes`, { text, visibility: canShare ? visibility : "private" });
      setText("");
      qc.invalidateQueries({ queryKey: ["job-notes", jobId] });
    } catch (e) { setErr(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await http.delete(`/jobs/${jobId}/notes/${id}`); qc.invalidateQueries({ queryKey: ["job-notes", jobId] }); }
    catch (e) { setErr(formatErr(e?.response?.data?.detail)); }
  };

  return (
    <div className="rmms-card p-5" data-testid="job-notes-card">
      <div className="overline mb-3 flex items-center gap-2"><NotePencil size={13} /> Notes</div>

      <div className="space-y-2 mb-4 max-h-72 overflow-y-auto">
        {notes.length === 0 && <div className="text-sm text-slate-400">No notes yet.</div>}
        {notes.map((n) => (
          <div key={n.id} data-testid={`note-${n.id}`} className="border border-slate-200 rounded-md p-2.5 bg-slate-50/50">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                <span className="font-medium text-slate-700">{n.author_name || "Unknown"}</span>
                <span>·</span>
                <span className="font-mono-data">{fmtDate(n.created_at)}</span>
                {n.visibility === "private" ? (
                  <span className="inline-flex items-center gap-0.5 text-amber-600" title="Only you can see this"><Lock size={10} weight="fill" /> Private</span>
                ) : (
                  <span className="inline-flex items-center gap-0.5 text-blue-600" title="Visible to everyone"><Globe size={10} weight="fill" /> Shared</span>
                )}
              </div>
              {(n.author_id === currentUserId || canShare) && (
                <button onClick={() => remove(n.id)} data-testid={`note-delete-${n.id}`} className="text-slate-400 hover:text-rose-600 shrink-0"><Trash size={13} /></button>
              )}
            </div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap break-words">{n.text}</div>
          </div>
        ))}
      </div>

      <div className="border-t border-slate-200 pt-3 space-y-2">
        <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="note-text-input" rows={2}
          placeholder="Add a note…" className="rmms-input h-auto py-2 resize-y w-full" />
        <div className="flex items-center justify-between gap-2">
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
            <button type="button" onClick={() => setVisibility("private")} data-testid="note-visibility-private"
              className={`px-2.5 py-1.5 inline-flex items-center gap-1 ${visibility === "private" ? "bg-amber-50 text-amber-700 font-medium" : "text-slate-500 hover:bg-slate-50"}`}>
              <Lock size={11} /> Private
            </button>
            {canShare && (
              <button type="button" onClick={() => setVisibility("shared")} data-testid="note-visibility-shared"
                className={`px-2.5 py-1.5 inline-flex items-center gap-1 border-l border-slate-200 ${visibility === "shared" ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-500 hover:bg-slate-50"}`}>
                <Globe size={11} /> Shared
              </button>
            )}
          </div>
          <button onClick={add} disabled={busy || !text.trim()} data-testid="note-add-button"
            className="h-9 px-3 rounded-md text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: "#002FA7" }}>
            <PaperPlaneTilt size={13} weight="bold" /> Add note
          </button>
        </div>
        {err && <div className="text-xs text-rose-600" data-testid="note-error">{err}</div>}
      </div>
    </div>
  );
}
